import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, limit } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdmin } from '../utils/roleUtils';
import { getAllRewardItems } from '../services/rewardItemsService';

/**
 * Admin component to manage reward items
 * Allows superadmins to create, edit, delete reward items and set unlock requirements
 */
const AdminRewardItems = () => {
  const { userRole } = useAuth();
  const [rewardItems, setRewardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    itemType: 'video_background', // 'video_background', 'audio_track'
    // Video background fields
    vimeoId: '',
    vimeoHash: '',
    // Audio track fields
    fileName: '',
    unlockType: 'default',
    unlockValue: '',
    order: 0
  });

  // Check superadmin access - must check before any conditional rendering
  const isSuperAdminUser = isSuperAdmin(userRole);

  const loadRewardItems = async () => {
    setLoading(true);
    try {
      const items = await getAllRewardItems();
      setRewardItems(items);
      setMessage('');
    } catch (error) {
      console.error('Error loading reward items:', error);
      setMessage(`Error loading items: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load reward items
  useEffect(() => {
    if (isSuperAdminUser) {
      loadRewardItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdminUser]);

  // Check superadmin access
  if (!isSuperAdminUser) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-red-700">Access denied. This page is only available to superadmins.</p>
        </div>
      </div>
    );
  }

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({
      id: '',
      name: '',
      itemType: 'video_background',
      vimeoId: '',
      vimeoHash: '',
      fileName: '',
      unlockType: 'default',
      unlockValue: '',
      order: rewardItems.length
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      id: item.id,
      name: item.name,
      itemType: item.itemType || 'video_background',
      vimeoId: item.vimeoId || '',
      vimeoHash: item.vimeoHash || '',
      fileName: item.fileName || '',
      unlockType: item.unlockRequirement?.type || 'default',
      unlockValue: item.unlockRequirement?.value || '',
      order: item.order || 0
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.id || !formData.name) {
      setMessage('Please fill in ID and Name fields');
      return;
    }

    // Type-specific validation
    if (formData.itemType === 'video_background') {
      if (!formData.vimeoId || !formData.vimeoHash) {
        setMessage('Please provide Vimeo ID and Hash for video backgrounds');
        return;
      }
    } else if (formData.itemType === 'audio_track') {
      if (!formData.fileName) {
        setMessage('Please provide a file name for audio tracks');
        return;
      }
    }

    if (formData.unlockType === 'level' && !formData.unlockValue) {
      setMessage('Please specify the level number for unlock requirement');
      return;
    }

    if (formData.unlockType === 'prompt' && !formData.unlockValue) {
      setMessage('Please specify the number of prompts for unlock requirement');
      return;
    }

    setMessage('Saving...');

    try {
      // Prepare unlock requirement
      const unlockRequirement = {
        type: formData.unlockType
      };
      
      if (formData.unlockType === 'level' || formData.unlockType === 'prompt') {
        unlockRequirement.value = parseInt(formData.unlockValue);
      } else if (formData.unlockType === 'course') {
        unlockRequirement.courseId = formData.unlockValue;
      }

      // Build document data based on item type
      const docData = {
        id: formData.id,
        name: formData.name,
        itemType: formData.itemType,
        unlockRequirement: unlockRequirement,
        order: parseInt(formData.order)
      };

      // Add type-specific fields
      if (formData.itemType === 'video_background') {
        docData.vimeoId = formData.vimeoId;
        docData.vimeoHash = formData.vimeoHash;
      } else if (formData.itemType === 'audio_track') {
        docData.fileName = formData.fileName;
      }

      // Save to Firestore
      const docRef = doc(db, 'reward_items', formData.id);
      await setDoc(docRef, docData);

      setMessage(`✓ Successfully saved ${formData.name}`);
      setIsModalOpen(false);
      await loadRewardItems();
    } catch (error) {
      console.error('Error saving item:', error);
      setMessage(`✗ Error: ${error.message}`);
    } finally {
      // Cleanup handled by component state
    }
  };

  const handleDelete = async (itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to delete "${itemName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setMessage(`Deleting ${itemName}...`);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'reward_items', itemId));
      
      setMessage(`✓ Successfully deleted ${itemName}`);
      await loadRewardItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      setMessage(`✗ Error deleting item: ${error.message}`);
    }
  };

  const unlockAllItemsForUser = async () => {
    if (!unlockEmail) {
      setMessage('Please enter a user email');
      return;
    }

    setIsUnlocking(true);
    setMessage(`Looking for user: ${unlockEmail}...`);
    
    try {
      // Find user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', unlockEmail), limit(1));
      const usersSnapshot = await getDocs(q);
      
      if (usersSnapshot.empty) {
        setMessage(`✗ User not found: ${unlockEmail}`);
        setIsUnlocking(false);
        return;
      }
      
      const userDoc = usersSnapshot.docs[0];
      const userId = userDoc.id;
      setMessage(`✓ Found user: ${userId}\nUnlocking all items...`);
      
      // Get all reward items
      const rewardItemsSnapshot = await getDocs(collection(db, 'reward_items'));
      
      if (rewardItemsSnapshot.empty) {
        setMessage(`✗ No reward items found.`);
        setIsUnlocking(false);
        return;
      }
      
      // Get user's existing positions/scales
      const userRewardItemsRef = doc(db, 'user_reward_items', userId);
      const userRewardItemsCollection = await getDocs(collection(db, 'user_reward_items'));
      const existingUserDoc = userRewardItemsCollection.docs.find(d => d.id === userId);
      const existingItems = existingUserDoc ? existingUserDoc.data().items || [] : [];
      
      // Create map of existing items
      const existingItemsMap = {};
      existingItems.forEach(item => {
        existingItemsMap[item.id] = item;
      });
      
      // Create unlocked items array
      const unlockedItems = [];
      rewardItemsSnapshot.forEach(itemDoc => {
        const itemData = itemDoc.data();
        const existingItem = existingItemsMap[itemDoc.id];
        
        unlockedItems.push({
          id: itemDoc.id,
          image: existingItem?.image || '',
          name: itemData.name,
          position: existingItem?.position || { x: 50, y: 50 },
          scale: existingItem?.scale || 1.0,
          unlocked: true // Force unlock
        });
      });
      
      // Save to Firestore
      await setDoc(userRewardItemsRef, { items: unlockedItems });
      
      setMessage(`✓ Successfully unlocked ${unlockedItems.length} items for ${unlockEmail}!`);
      
    } catch (error) {
      console.error('Error unlocking items:', error);
      setMessage(`✗ Error: ${error.message}`);
    } finally {
      setIsUnlocking(false);
    }
  };

  const getUnlockDescription = (item) => {
    if (!item.unlockRequirement) return 'Unknown';
    
    switch (item.unlockRequirement.type) {
      case 'default':
        return 'Always unlocked';
      case 'level':
        return `Level ${item.unlockRequirement.value}`;
      case 'prompt':
        return `${item.unlockRequirement.value} prompts`;
      case 'course':
        return `Course: ${item.unlockRequirement.courseId}`;
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading reward items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Reward Items Management</h1>
        <button
          onClick={handleAddNew}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          + Add New Item
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.includes('✓') ? 'bg-green-50 text-green-800' :
          message.includes('✗') ? 'bg-red-50 text-red-800' :
          'bg-blue-50 text-blue-800'
        }`}>
          <pre className="whitespace-pre-wrap font-mono text-sm">{message}</pre>
        </div>
      )}

      {/* Unlock Items for User Section */}
      <div className="mb-8 p-6 border rounded-lg bg-white shadow">
        <h2 className="text-xl font-bold mb-4">Unlock All Items for User</h2>
        <p className="text-sm text-gray-600 mb-4">
          Manually unlock all reward items for a specific user (bypasses level requirements)
        </p>
        <div className="flex items-center space-x-4">
          <input
            type="email"
            value={unlockEmail}
            onChange={(e) => setUnlockEmail(e.target.value)}
            placeholder="Enter user email"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            disabled={isUnlocking}
          />
          <button
            onClick={unlockAllItemsForUser}
            disabled={isUnlocking || !unlockEmail}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isUnlocking ? 'Unlocking...' : 'Unlock All Items'}
          </button>
        </div>
      </div>

      {/* Reward Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rewardItems.map((item) => (
          <div key={item.id} className="border rounded-lg overflow-hidden bg-white shadow hover:shadow-lg transition-shadow">
            {/* Preview */}
            <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
              {item.itemType === 'video_background' ? (
                <div className="text-gray-400 text-center p-4">
                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Video Background</p>
                  {item.vimeoId && <p className="text-xs mt-1">{item.vimeoId}</p>}
                </div>
              ) : item.itemType === 'audio_track' ? (
                <div className="text-gray-400 text-center p-4">
                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  <p className="text-sm">Audio Track</p>
                  {item.fileName && <p className="text-xs mt-1">{item.fileName}</p>}
                </div>
              ) : (
                <div className="text-gray-400 text-center p-4">
                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">No image</p>
                </div>
              )}
            </div>

            {/* Item Details */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-lg">{item.name}</h3>
                {/* Item Type Badge */}
                <span className={`text-xs px-2 py-1 rounded ${
                  item.itemType === 'video_background' ? 'bg-purple-100 text-purple-800' :
                  item.itemType === 'audio_track' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {item.itemType === 'video_background' ? 'Video' :
                   item.itemType === 'audio_track' ? 'Audio' :
                   'Unknown'}
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-600 mb-4">
                <p><span className="font-medium">ID:</span> {item.id}</p>
                {item.itemType === 'video_background' && (
                  <>
                    <p><span className="font-medium">Vimeo ID:</span> {item.vimeoId || 'N/A'}</p>
                    <p><span className="font-medium">Hash:</span> {item.vimeoHash || 'N/A'}</p>
                  </>
                )}
                {item.itemType === 'audio_track' && (
                  <p><span className="font-medium">File:</span> {item.fileName || 'N/A'}</p>
                )}
                <p><span className="font-medium">Unlock:</span> {getUnlockDescription(item)}</p>
                <p><span className="font-medium">Order:</span> {item.order}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.name)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {rewardItems.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-4">No reward items found.</p>
          <button
            onClick={handleAddNew}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Create Your First Item
          </button>
        </div>
      )}

      {/* Edit/Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {editingItem ? 'Edit Reward Item' : 'Add New Reward Item'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                {/* Item ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item ID *
                  </label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    disabled={!!editingItem}
                    placeholder="e.g., typewriter, camera"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  {editingItem && (
                    <p className="text-xs text-gray-500 mt-1">ID cannot be changed after creation</p>
                  )}
                </div>

                {/* Item Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Typewriter"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Item Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Type *
                  </label>
                  <select
                    value={formData.itemType}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        itemType: e.target.value,
                        // Clear type-specific fields when switching types
                        vimeoId: '',
                        vimeoHash: '',
                        fileName: ''
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="video_background">Video Background</option>
                    <option value="audio_track">Audio Track</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.itemType === 'video_background' && 'Video backgrounds for the Writers Room scene'}
                    {formData.itemType === 'audio_track' && 'Audio tracks for the music player'}
                  </p>
                </div>

                {/* Video Background Fields */}
                {formData.itemType === 'video_background' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vimeo Video ID *
                      </label>
                      <input
                        type="text"
                        value={formData.vimeoId}
                        onChange={(e) => setFormData({ ...formData, vimeoId: e.target.value })}
                        placeholder="e.g., 1127381441"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The numeric ID from the Vimeo video URL
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vimeo Hash *
                      </label>
                      <input
                        type="text"
                        value={formData.vimeoHash}
                        onChange={(e) => setFormData({ ...formData, vimeoHash: e.target.value })}
                        placeholder="e.g., 7351e669fd"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The hash parameter from the Vimeo embed URL (h parameter)
                      </p>
                    </div>
                  </>
                )}

                {/* Audio Track Fields */}
                {formData.itemType === 'audio_track' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Audio File Name *
                    </label>
                    <input
                      type="text"
                      value={formData.fileName}
                      onChange={(e) => setFormData({ ...formData, fileName: e.target.value })}
                      placeholder="e.g., ambient.mp3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The filename of the audio file (must exist in /images/writers-room/audio/)
                    </p>
                  </div>
                )}

                {/* Unlock Requirement Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unlock Requirement Type *
                  </label>
                  <select
                    value={formData.unlockType}
                    onChange={(e) => setFormData({ ...formData, unlockType: e.target.value, unlockValue: '' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="default">Default (Always Unlocked)</option>
                    <option value="level">Level Completion</option>
                    <option value="prompt">Prompt Completion</option>
                    <option value="course">Course Completion</option>
                  </select>
                </div>

                {/* Unlock Value (conditional) */}
                {(formData.unlockType === 'level' || formData.unlockType === 'prompt') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.unlockType === 'level' ? 'Level Number *' : 'Number of Prompts *'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.unlockValue}
                      onChange={(e) => setFormData({ ...formData, unlockValue: e.target.value })}
                      placeholder={formData.unlockType === 'level' ? 'e.g., 5' : 'e.g., 10'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.unlockType === 'level' 
                        ? 'Item unlocks after user completes this many levels'
                        : 'Item unlocks after user completes this many prompts'}
                    </p>
                  </div>
                )}

                {formData.unlockType === 'course' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course ID *
                    </label>
                    <input
                      type="text"
                      value={formData.unlockValue}
                      onChange={(e) => setFormData({ ...formData, unlockValue: e.target.value })}
                      placeholder="e.g., course_basics"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Item unlocks after user completes this specific course
                    </p>
                  </div>
                )}

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Lower numbers appear first in lists
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex space-x-4">
                <button
                  onClick={handleSave}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Save Item
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRewardItems;
